/**
 * ListRow — soft rounded row: orange-soft circular icon, bold title, small subtitle,
 * optional real metadata columns, and a compact action on the right.
 * Hover or selected state lifts the row (surface step, offset, 1px up).
 */
import React from "react";
import { Link } from "react-router-dom";

export interface ListRowProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Real metadata only (e.g. University, Year, Size). Hidden on mobile. */
  meta?: { label: string; value: React.ReactNode }[];
  action?: React.ReactNode;
  to?: string;
  selected?: boolean;
  className?: string;
}

const LIFT =
  "transition-[transform,background-color,box-shadow] duration-200 hover:bg-lift hover:translate-x-1 hover:-translate-y-px focus-visible:bg-lift focus-visible:translate-x-1 hover:shadow-soft motion-reduce:transition-none motion-reduce:hover:transform-none";

export function ListRow({ icon, title, subtitle, meta, action, to, selected = false, className = "" }: ListRowProps) {
  const body = (
    <>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-soft text-primary" aria-hidden="true">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-bold text-foreground">{title}</span>
        {subtitle && <span className="block truncate text-sm text-muted-foreground">{subtitle}</span>}
      </span>
      {meta && meta.length > 0 && (
        <span className="hidden shrink-0 items-center gap-6 md:flex">
          {meta.map((m) => (
            <span key={m.label} className="text-sm">
              <span className="block text-xs text-muted-foreground">{m.label}</span>
              <span className="block font-medium text-foreground">{m.value}</span>
            </span>
          ))}
        </span>
      )}
      {action && <span className="shrink-0">{action}</span>}
    </>
  );
  const classes = `flex min-h-14 w-full items-center gap-3 rounded-tile bg-soft px-4 py-3 ${LIFT} ${
    selected ? "bg-lift translate-x-1 -translate-y-px border border-border" : ""
  } ${className}`.trim();

  return to ? (
    <Link to={to} className={`${classes} focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}>
      {body}
    </Link>
  ) : (
    <div className={classes}>{body}</div>
  );
}
